import React, {useEffect, useRef, useState} from 'react'
import Drawer from 'react-modern-drawer'
import { createGitLabIntegration } from '../../../../Integrations/Gitlab'
import {useAuth} from '../../../../modules/auth'
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from 'firebase/firestore'
import db from '../../../../../services/firebase'
import {useLocation, useNavigate, useParams} from 'react-router-dom'
import {showErrorToast, showInfoToast, showSuccessToast} from 'app/components/ShowToast'
import DrawerHeader from '../drawer-components/DrawerHeader'
import {DrawerContent} from '../drawer-components/DrawerContent'
import Overlay from 'app/components/Overlay'
import DrawerFooter from '../drawer-components/DrawerFooter'
import InfoCard from '../drawer-components/InfoCard'
import {AddDrawerIntegrationProps, GitlabRepository} from '../utils/types'

const mapToRepository = (project: any): GitlabRepository => ({
  name: project.name || 'Unnamed Repository',
  html_url: project.web_url || '',
  private: project.visibility === 'private',
  description: project.description || null,
  created_at: project.created_at || new Date().toISOString(),
  last_activity_at: project.last_activity_at || new Date().toISOString(),
  pushed_at: project.last_activity_at || new Date().toISOString(),
  stargazers_count: project.star_count || 0,
  forks_count: project.forks_count || 0,
  open_issues_count: project.open_issues_count || 0,
  watchers_count: project.star_count || 0,
  language: project.language || null,
  default_branch: project.default_branch || 'main',
  is_repo: true,
  repo_name: project.name || 'Unnamed Repository',
  web_url: project.web_url || '',
  platform: 'GitLab',
  visibility: project.visibility || 'private',
  size: project.size || 0,
  star_count: project.star_count || 0,
  watchers: project.star_count || 0,
  forks: project.forks_count || 0,
  open_issues: project.open_issues_count || 0,
  namespace: project.namespace || {},
  owner: {
    login: project.namespace?.name || '',
    id: project.namespace?.id || 0,
    html_url: project.namespace?.web_url || '',
    type: project.namespace?.kind || 'user',
  },
})

const GITLAB_CLIENT_ID = process.env.REACT_APP_GITLAB_CLIENT_ID || ''
const GITLAB_REDIRECT_URI = process.env.REACT_APP_GITLAB_REDIRECT_URI || ''
const GITLAB_URL = process.env.REACT_APP_GITLAB_URL || ''

const GitlabDrawer: React.FC<AddDrawerIntegrationProps> = ({id, isOpen, onClose}) => {
  const [isLoading, setIsLoading] = useState(false)
  const [groupSelecting, setGroupSelecting] = useState(false)
  const [openAccordion, setOpenAccordion] = useState(false)
  const [openAccordion2, setOpenAccordion2] = useState(false)
  const {currentUser} = useAuth()
  const [showAlert, setShowAlert] = useState(false)
  const drawerTopRef = useRef<HTMLDivElement>(null)
  const [integration, setIntegration] = useState<any[]>([])
  const [selectedGroup, setSelectedGroup] = useState('')
  const [isGroupLocked, setIsGroupLocked] = useState(false)
  const [accessTokenGitlab, setAccessTokenGitlab] = useState<string | null>(
    localStorage.getItem('gitlab_access_token')
  )
  const [formErrors, setFormErrors] = useState<Record<string, string>>({})
  const navigate = useNavigate()
  const location = useLocation()
  const {code} = useParams()
  const queryParams = new URLSearchParams(location.search)
  const integrationIdFromUrl = queryParams.get('id') || id

  const isConnected = integration.length > 0 && integration[0]?.status === 'Connected'

  useEffect(() => {
    if (code) {
      localStorage.setItem('gitlab_access_token', code)
      navigate('/integrations', {replace: true})
    }
  }, [code, navigate])

  useEffect(() => {
    if (isOpen && id) {
      getIntegration();
    }
    if (isOpen && !selectedGroup) {
      setFormErrors((prev) => ({ ...prev, selectedWorkspace: 'Please select a group' }));
    } else if (isOpen && selectedGroup) {
      setFormErrors((prev) => ({ ...prev, selectedWorkspace: '' }));
    }
  }, [id, isOpen, selectedGroup]);

  const getIntegration = () => {
    try {
      const tenantId = currentUser?.tenantId || ''
      if (!integrationIdFromUrl) return
      const integrationRef = doc(db, 'tenants', tenantId, 'integrations', integrationIdFromUrl)
      const unsubscribe = onSnapshot(integrationRef, (doc) => {
        if (doc.exists()) {
          const data = doc.data()
          if (data.name === 'GitLab') {
            const integrationData = {
              ...data,
              id: doc.id,
              name: data.name,
              accessToken: data.accessToken,
              groups: data.groups || [],
            }
            setIntegration([integrationData])
            setAccessTokenGitlab(data.accessToken)
            if (data.groups?.length > 0 && data.groupId) {
              setSelectedGroup(data.groupId)
              setIsGroupLocked(true)
            }
          }
        }
      })
      return unsubscribe
    } catch (error) {
      console.error('Error getting integration:', error)
    }
  }

  useEffect(() => {
    if (showAlert && drawerTopRef.current) {
      drawerTopRef.current.scrollIntoView({behavior: 'smooth'})
    }
  }, [showAlert])

  const handleConnect = async () => {
    if (!accessTokenGitlab) {
      setIsLoading(true)
      localStorage.setItem('pendingIntegration', 'GitLab')
      const encodedRedirectUri = encodeURIComponent(GITLAB_REDIRECT_URI)
      const authUrl = `${GITLAB_URL}/oauth/authorize?client_id=${GITLAB_CLIENT_ID}&redirect_uri=${encodedRedirectUri}&response_type=code&scope=api+read_user+read_repository`
      window.location.href = authUrl
      return
    }
  }

  const handleClose = () => {
    setShowAlert(false)
    onClose()
  }

  const handleSubmit = async (projects: GitlabRepository[]): Promise<number> => {
    setGroupSelecting(true)
    let addedProjectsCount = 0

    try {
      for (const project of projects) {
        const newAsset = {
          is_repo: true,
          repo_name: project.name || 'Unnamed Repository',
          repo_link: project.web_url || '',
          platform: 'GitLab',
          visibility: project.visibility || 'private',
          description: project.description || '',
          created_at: project.created_at || new Date().toISOString(),
          updated_at: project.last_activity_at || new Date().toISOString(),
          pushed_at: project.pushed_at || new Date().toISOString(),
          size: project.size || 0,
          stars: project.star_count || 0,
          watchers: project.watchers || 0,
          language: project.language || '',
          forks: project.forks_count || 0,
          open_issues: project.open_issues_count || 0,
          default_branch: project.default_branch || 'main',
          owner: {
            login: project.owner?.login || project.namespace?.name || '',
            id: project.owner?.id || project.namespace?.id || 0,
            html_url: project.owner?.html_url || project.namespace?.web_url || '',
            type: project.owner?.type || project.namespace?.kind || 'group',
          },
          tenantId: currentUser?.tenantId || '',
          uid: currentUser?.uid || '',
          createdAt: serverTimestamp(),
          audit_scope: true,
        }

        if (currentUser && currentUser.tenantId) {
          const tenantRef = doc(db, 'tenants', currentUser.tenantId)
          const policyRef = collection(tenantRef, 'assets')

          const querySnapshot = await getDocs(
            query(policyRef, where('repo_link', '==', project.web_url))
          )

          if (querySnapshot.empty) {
            await addDoc(policyRef, newAsset)
            addedProjectsCount++

            const currentDate = new Date()
            const todaysDate = `${
              currentDate.getMonth() + 1
            }/${currentDate.getDate()}/${currentDate.getFullYear()}`

            const activitiesRef = collection(tenantRef, 'activities')
            await addDoc(activitiesRef, {
              message: `${currentUser?.userName} added a new asset '${project.name}'`,
              timestamp: serverTimestamp(),
              tenantId: currentUser?.tenantId,
              uid: currentUser?.uid,
              action: 'Asset added',
              target: 'Asset Management',
              email: currentUser?.email,
              username: currentUser?.userName,
              date: todaysDate,
            })
          }
        }
      }

      if (addedProjectsCount > 0) {
        showSuccessToast({message: 'New projects added successfully'})
      } else {
        showInfoToast({message: 'No new projects were added'})
      }

      return addedProjectsCount
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      console.error('Error submitting projects:', errorMessage)
      showErrorToast({message: `Failed to add projects: ${errorMessage}`})
      return 0
    } finally {
      setGroupSelecting(false)
    }
  }

  const saveGroupMembers = async (members: any[], groupId: string, tenantId: string) => {
    try {
      const accessReviewRef = doc(db, 'tenants', tenantId, 'access_review', 'gitlab')
      const membersMap: {[key: string]: any} = {}

      for (const member of members) {
        if (member.username) {
          membersMap[member.username] = {
            platform: 'GitLab',
            username: member.username || 'Unknown User',
            name: member.name || member.username || 'Unknown User',
            isTwoFactorEnabled: member.mfaEnabled || false,
            groupId,
            tenantId,
            uid: currentUser?.uid || '',
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            status: 'active',
            accountRole: member.role || '',
            group: {
              id: groupId,
              name: integration[0]?.groups.find((g: any) => g.id === parseInt(groupId))?.name || '',
            },
          }
        } 
      }

      const gitlabDoc = await getDoc(accessReviewRef)

      if (gitlabDoc.exists()) {
        await updateDoc(accessReviewRef, {
          members: {
            ...gitlabDoc.data().members,
            ...membersMap,
          },
          updatedAt: serverTimestamp(),
        })
      } else {
        await setDoc(accessReviewRef, {
          platform: 'GitLab',
          members: membersMap,
          tenantId,
          uid: currentUser?.uid || '',
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          groupId,
        })
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      console.error('Error saving group members:', errorMessage)
      showErrorToast({message: 'Failed to save group members'})
      throw error
    }
  }

  const handleGroupSelect = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const groupId = e.target.value
    setSelectedGroup(groupId)

    if (!groupId) {
      setFormErrors((prev) => ({...prev, selectedGroup: 'Please select a group'}))
      return
    }

    setFormErrors((prev) => ({...prev, selectedGroup: ''}))

    try {
      setGroupSelecting(true)
      const tenantId = currentUser?.tenantId || ''
      if (!integrationIdFromUrl || !tenantId) return

      const integrationRef = doc(db, 'tenants', tenantId, 'integrations', integrationIdFromUrl)
      const selectedGroupData = integration[0].groups.find((g: any) => g.id === parseInt(groupId))

      await updateDoc(integrationRef, {
        groupId,
        selectedGroup: selectedGroupData,
      })

      const gitlabIntegration = createGitLabIntegration(
        accessTokenGitlab || '',
      )

      const [projectsResponse, membersResponse] = await Promise.all([
        gitlabIntegration.getGroupProjects(groupId),
        gitlabIntegration.checkUser2FA(parseInt(groupId)),
      ])

      if (projectsResponse.success && projectsResponse.projects) {
        const mappedProjects = projectsResponse.projects.map(mapToRepository)
        await handleSubmit(mappedProjects)
      }

      if (membersResponse.success && membersResponse.users) {
        await saveGroupMembers(membersResponse.users, groupId, tenantId)
      }

      setIsGroupLocked(true)
      showSuccessToast({message: 'Group selected and data loaded successfully'})
    } catch (error) {
      console.error('Error handling group selection:', error)
      showErrorToast({message: 'Failed to process group selection'})
    } finally {
      setGroupSelecting(false)
    }
  }

  const handleDisconnect = async () => {
    try {
      setIsLoading(true)
      const tenantId = currentUser?.tenantId || ''
      if (!integrationIdFromUrl || !tenantId) return
      const tenantRef = doc(db, 'tenants', tenantId)

      const assetsRef = collection(tenantRef, 'assets')
      const gitlabAssetsQuery = query(assetsRef, where('platform', '==', 'GitLab'))
      const gitlabAssetsSnapshot = await getDocs(gitlabAssetsQuery)

      const deleteAssetPromises = gitlabAssetsSnapshot.docs.map(async (assetDoc) => {
        await deleteDoc(doc(db, 'tenants', tenantId, 'assets', assetDoc.id))
      })

      await Promise.all(deleteAssetPromises)

      const integrationRef = doc(db, 'tenants', tenantId, 'integrations', integrationIdFromUrl)
      await deleteDoc(integrationRef)

      const accessReviewRef = doc(db, 'tenants', tenantId, 'access_review', 'gitlab')
      await deleteDoc(accessReviewRef)

      const vendorsRef = collection(tenantRef, 'vendors')
      const gitlabVendorQuery = query(vendorsRef, where('vendor_name', '==', 'GitLab'))
      const gitlabVendorSnapshot = await getDocs(gitlabVendorQuery)

      const deleteVendorPromises = gitlabVendorSnapshot.docs.map(async (vendorDoc) => {
        await deleteDoc(doc(db, 'tenants', tenantId, 'vendors', vendorDoc.id))
      })

      await Promise.all(deleteVendorPromises)

      localStorage.removeItem('gitlab_access_token')

      const activitiesRef = collection(tenantRef, 'activities')
      const currentDate = new Date()
      const todaysDate = `${
        currentDate.getMonth() + 1
      }/${currentDate.getDate()}/${currentDate.getFullYear()}`
      await addDoc(activitiesRef, {
        message: `${currentUser?.userName} disconnected GitLab integration`,
        timestamp: serverTimestamp(),
        tenantId: currentUser?.tenantId,
        uid: currentUser?.uid,
        action: 'Integration disconnected',
        target: 'Integration Management',
        email: currentUser?.email,
        username: currentUser?.userName,
        date: todaysDate,
      })

      showSuccessToast({message: 'GitLab integration disconnected'})

      setIntegration([])
      setSelectedGroup('')
      setIsGroupLocked(false)
      handleClose()
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      console.error('Error disconnecting GitLab:', errorMessage)
      showErrorToast({message: 'Failed to disconnect GitLab'})
    } finally {
      setIsLoading(false)
    }
  }

  const WorkspaceSelector = () => {
    if (!integration[0]?.groups?.length) return null

    return (
      <div className='mb-4'>
        <label className='fw-semibold mb-2 fs-6'>Select Group</label>
        <select
          className='form-select'
          value={selectedGroup}
          onChange={handleGroupSelect}
          disabled={isGroupLocked}
        >
          <option value=''>Select Group</option>
          {integration[0].groups.map((group: any) => (
            <option key={group.id} value={group.id}>
              {group.name}
            </option>
          ))}
        </select>
        {formErrors.selectedGroup && (
          <span className='text-danger'>{formErrors.selectedGroup}</span>
        )}
        {isGroupLocked && (
          <small className='text-muted'>
            Group selection is locked. To change, disconnect the integration.
          </small>
        )}
      </div>
    )
  }

  return (
    <Drawer open={isOpen} onClose={handleClose} direction='right' className='' size='45%'>
      <DrawerHeader title='GitLab' onClose={onClose} />
      <div
        className='scroll-y overflow-auto p-6 mb-10'
        tabIndex={-1}
        style={{
          height: 'calc(100vh - 120px)',
          paddingBottom: '80px',
        }}
      >
        <div className='form'>
          <DrawerContent
            title='GitLab'
            description='Code Collaboration & Version Control Platform'
            logo='/media/integrations/gitlab.svg'
            integration={integration}
            openAccordion={openAccordion}
            setOpenAccordion={setOpenAccordion}
            openAccordion2={openAccordion2}
            setOpenAccordion2={setOpenAccordion2}
            permissionInfo='SecureSlate will be granted permissions to read GitLab repositories, issues, and merge requests via OAuth 2.0, respecting user permissions.'
            accessTypeinfo='Requires an active GitLab integration with sufficient permissions to access repository and project data.'
          />
          <div className='mb-8 d-flex justify-content-between align-items-center'>
            <div className='fs-6 fw-bold text-bold'>
              {isConnected ? 'GitLab Connected to SecureSlate' : 'Connect GitLab to SecureSlate'}{' '}
              <Overlay content='Upon successful connection, all projects within your designated GitLab group will be automatically imported and presented within the Asset Repository section of the platform for your review and management.' />
            </div>
            {groupSelecting && (
              <span className='badge px-3 rounded-pill ms-4' style={{border: '1px solid #E1E1E1'}}>
                <span className='fs-7'>Updating...</span>
              </span>
            )}
          </div>
          <WorkspaceSelector />
          {!isConnected &&
          <InfoCard
            type='info'
            title='Connect GitLab to SecureSlate'
            description='Click on the ‘Connect’ button below. You will be redirected to GitLab to authenticate via OAuth and authorize SecureSlate. Once authorized, projects will be automatically fetched and configured.'
          />}
        </div>
      </div>
      <DrawerFooter
        isConnected={isConnected}
        isLoading={isLoading}
        onConnect={handleConnect}
        onDisconnect={handleDisconnect}
        customActions={!isConnected}
      />
    </Drawer>
  )
}

export default GitlabDrawer
